import { audio } from '../../../audio/AudioManager.js';
import { SaveManager } from '../../../state/SaveManager.js';

export interface ApplicationLifecycleSubscriber {
  readonly onSuspend?: () => void;
  readonly onResume?: () => void;
}

type BackgroundReason = 'visibility' | 'blur' | 'pagehide';

/** Owns browser and PWA lifecycle listeners once for the whole game. */
class ApplicationLifecycle {
  private game?: Phaser.Game;
  private started = false;
  private suspended = false;
  private readonly reasons = new Set<BackgroundReason>();
  private readonly subscribers = new Set<ApplicationLifecycleSubscriber>();

  start(game: Phaser.Game) {
    if (this.started) return;
    this.started = true;
    this.game = game;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pointerdown', this.handleUserGesture, { passive: true });
    window.addEventListener('keydown', this.handleUserGesture);
    this.handleVisibilityChange();
  }

  subscribe(subscriber: ApplicationLifecycleSubscriber) {
    this.subscribers.add(subscriber);
    if (this.suspended) subscriber.onSuspend?.();
    return () => this.subscribers.delete(subscriber);
  }

  isSuspended() { return this.suspended; }

  private updateSuspendedState() {
    const nextSuspended = this.reasons.size > 0;
    if (nextSuspended === this.suspended) return;
    this.suspended = nextSuspended;
    if (nextSuspended) {
      SaveManager.save();
      if (this.game) this.game.input.enabled = false;
      audio.pause('background');
      this.subscribers.forEach(subscriber => subscriber.onSuspend?.());
      return;
    }

    if (this.game) this.game.input.enabled = true;
    this.subscribers.forEach(subscriber => subscriber.onResume?.());
    void audio.resume('background');
  }

  private setReason(reason: BackgroundReason, active: boolean) {
    if (active) this.reasons.add(reason);
    else this.reasons.delete(reason);
    this.updateSuspendedState();
  }

  private readonly handleVisibilityChange = () => {
    this.setReason('visibility', document.visibilityState === 'hidden');
  };

  private readonly handleBlur = () => this.setReason('blur', true);
  private readonly handleFocus = () => this.setReason('blur', false);

  private readonly handlePageHide = () => {
    SaveManager.save();
    this.setReason('pagehide', true);
  };

  private readonly handlePageShow = () => this.setReason('pagehide', false);
  private readonly handleUserGesture = () => { void audio.resumeFromGesture(); };
}

export const applicationLifecycle = new ApplicationLifecycle();
